// src/component/home/HomeNoticeModal.jsx
//
// 홈 공지 팝업. 앱(lib/utils/app_dialogs/notice.dart,
// lib/utils/modal_bottom_sheet/main_notice.dart)과 같은 규칙을 웹에 적용한다.
//  - is_required 면 배경 클릭·ESC 로 못 닫는다 (닫기 버튼은 남긴다)
//  - '오늘 하루 보지 않기' 는 날짜를 저장해 그날 안 뜨게 한다
//
// display_type 은 웹에서 «레이아웃» 으로 쓰지 않는다 — DIALOG·BOTTOM_SHEET 둘 다
// 가운데 알림창으로 띄운다. 아래에서 올라오는 시트는 엄지로 닿는 화면 아래쪽이
// 있는 모바일의 관용구라, 넓은 브라우저 창에서는 화면 바닥에 붙어 잘린 패널로
// 읽힌다. 관리자가 고른 값은 그대로 내려오고 앱(모바일)은 계속 그 값을 따른다.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

import { navigateToTarget, hasTarget } from "../../helpers/targetNavigator.js";

const HIDDEN_DATE_KEY = "korehalal_notice_hidden_date";

function today() {
  // 사용자의 로컬 날짜 기준 — 앱과 같은 yyyy-MM-dd.
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 오늘 하루 숨기기가 걸려 있으면 false. localStorage 를 못 쓰면 그냥 보여준다. */
export function shouldShowNotice(notice) {
  if (!notice || !String(notice.title ?? "").trim()) return false;

  try {
    return localStorage.getItem(HIDDEN_DATE_KEY) !== today();
  } catch {
    return true;
  }
}

export default function HomeNoticeModal({ notice, categories, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dontShowToday, setDontShowToday] = useState(false);

  const isRequired = notice?.is_required === true;
  const showMore = hasTarget(notice);

  // 열려 있는 동안 배경 스크롤을 막는다 (CustomDialog 와 같은 방식).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (isRequired) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isRequired, onClose]);

  const toggleDontShowToday = () => {
    const next = !dontShowToday;
    setDontShowToday(next);
    try {
      if (next) localStorage.setItem(HIDDEN_DATE_KEY, today());
      else localStorage.removeItem(HIDDEN_DATE_KEY);
    } catch {
      // 사파리 프라이빗 모드 등 — 숨기기만 못 할 뿐 공지는 정상 동작.
    }
  };

  const handleShowMore = () => {
    onClose();
    navigateToTarget(notice, navigate, categories);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[60] animate-fade-in"
        onClick={isRequired ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-label={notice?.title ?? "Notice"}
      >
        <div className="animate-pop-in bg-bg-primary w-full max-w-sm rounded-2xl shadow-2xl flex flex-col pointer-events-auto overflow-hidden">
          <div className="flex items-start justify-between px-5 pt-5">
            <h2 className="flex-1 min-w-0 text-heading-sm text-text-primary break-words">
              {notice?.title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="ml-3 -mr-1 p-1 rounded-full hover:bg-bg-secondary text-text-tertiary"
              aria-label={t("home.notice_close")}
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-5 pt-4 max-h-[60vh] overflow-y-auto">
            {notice?.image ? (
              <img
                src={notice.image}
                alt=""
                className="w-full rounded-lg object-cover aspect-[21/9] mb-4"
              />
            ) : null}
            <p className="text-body-md text-text-secondary whitespace-pre-wrap break-words">
              {notice?.body}
            </p>
          </div>

          {/* 체크 표시는 항상 자리를 잡아두고 투명도만 바꾼다 — 글리프가
              나타났다 사라지면 박스가 흔들린다. 색 토큰은 -primary 까지
              써야 한다: bg-bg-interactive / border-border-interactive 는
              tokens.js 에 없는 이름이라 Tailwind 가 클래스를 안 만들었고,
              그래서 «체크하면 흰 체크가 흰 배경 위»로 안 보였다. 접미사가
              두 단어인 토큰은 클래스에서 camelCase 다(secondaryHovered) —
              tailwind.config.js 의 toCamel 때문. 여기선 그 함정을 피해
              한 단어 토큰만 쓴다. */}
          <button
            type="button"
            role="checkbox"
            aria-checked={dontShowToday}
            onClick={toggleDontShowToday}
            className="group flex items-center gap-2 px-5 pt-4 text-body-sm text-text-secondary cursor-pointer"
          >
            <span
              className={[
                "w-5 h-5 rounded border flex items-center justify-center shrink-0 transition",
                "group-active:scale-95",
                dontShowToday
                  ? "bg-bg-interactive-primary border-border-interactive-primary text-white"
                  : "border-border-tertiary group-hover:border-border-interactive-secondary",
              ].join(" ")}
              aria-hidden="true"
            >
              <Check
                size={14}
                strokeWidth={3}
                className={["transition-opacity", dontShowToday ? "opacity-100" : "opacity-0"].join(" ")}
              />
            </span>
            <span className="transition-colors group-hover:text-text-primary">
              {t("home.notice_dont_show_today")}
            </span>
          </button>

          <div className="flex gap-2 p-4 pt-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-bg-secondary hover:bg-bg-tertiary text-text-primary text-body-md font-semibold"
            >
              {t("home.notice_close")}
            </button>
            {showMore ? (
              <button
                type="button"
                onClick={handleShowMore}
                className="flex-1 py-2.5 rounded-xl bg-black hover:bg-gray-800 text-white text-body-md font-semibold"
              >
                {t("home.notice_show_more")}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
